import { flatten, get } from '@libs/shared';
import { AbstractTreeNode, GenericTreeNode } from '~/interfaces';

/**
 * 叶子节点
 *
 * @param node
 */
export const isLeafNode = (node: AbstractTreeNode) => {
    return node.children === undefined || node.children.length === 0;
};

/**
 * 遍历所有节点，并将节点收集到一个数组中.
 *
 * @param nodes
 * @param order 参数可用于指定遍历规则
 * * `pre` 前序遍历 （默认）
 * * `post` 后续遍历
 * * `leaf-only` 忽略内部节点，只收集叶子节点
 */
export const collectNodes = <T extends AbstractTreeNode>(
    nodes: T[],
    order: 'pre' | 'post' | 'leaf-only' = 'pre'
) => {
    const dfs = (nodes: T[]) => {
        if (!nodes) {
            return;
        }
        for (const node of nodes) {
            if (isLeafNode(node)) {
                result.push(node);
            } else {
                if (order === 'pre') {
                    result.push(node);
                    dfs(node.children as T[]);
                } else if (order === 'post') {
                    dfs(node.children as T[]);
                    result.push(node);
                } else {
                    dfs(node.children as T[]);
                }
            }
        }
    };

    const result: T[] = [];
    dfs(nodes);
    return result;
};

/**
 * N叉树的层序遍历
 *
 * @param root N叉树的根节点 NodeType: {[key: string]: any, childrenKey: NodeType[]}
 * @param setting 自定义配置
 * @param setting.childrenKey N叉树的子节点数组的key，默认为 children
 * @param setting.shouldFlatten 是否要将层序遍历结果拍平，默认不拍平
 * @param setting.callback 遍历N叉树某个节点时的自定义回调，
 *                         node 为当前遍历节点；
 *                         isBreak 标识是否需要继续遍历当前节点的子节点，默认 false；
 *                         isCurrentNodeValid 标识当前节点是否是预期中的有效节点，默认为 true；
 * @return 返回由有效节点组成的二维数组，数组索引对应该层的遍历结果；或是其拍平后的结果
 */
export const levelTraverse = <NodeType>(
    root: NodeType,
    setting?: {
        childrenKey?: string;
        shouldFlatten?: boolean;
        callback?: (node?: NodeType) => { isBreak?: boolean; isCurrentNodeValid?: boolean } | void;
    }
): NodeType[][] | NodeType[] => {
    if (!root) {
        return [];
    }
    const { childrenKey = 'children', shouldFlatten = false, callback = () => {} } = setting || {};

    const result: NodeType[][] = [];
    // 层次队列，用于存储当前层的待遍历节点
    const queue: NodeType[] = [root];
    // 每进一次 while 就意味着向下遍历一层
    while (queue.length) {
        const level: NodeType[] = [];

        // 遍历当前层的节点
        for (let i = 0, len = queue.length; i < len; i++) {
            const currentNode = queue.shift();
            const { isBreak = false, isCurrentNodeValid = true } = callback(currentNode) || {};
            const children = get(currentNode, childrenKey, []);
            if (isCurrentNodeValid && currentNode) {
                level.push(currentNode);
            }

            if (!isBreak) {
                // 把当前遍历节点的有效子节点放进队列里
                for (const currentChild of children) {
                    if (currentChild) {
                        queue.push(currentChild);
                    }
                }
            }
        }

        // 把当前层的遍历结果放进结果数组里
        result.push(level);
    }
    return shouldFlatten ? flatten(result) : result;
};

/**
 * 通过dfs找到符合条件的node的path
 * @param node root
 * @param predicate 回调函数，return true 表示当前node符合条件
 * @returns 返回符合条件的node的path
 */
export const findPathWithDfs = <T extends Record<string, any>>(
    nodes: GenericTreeNode<T>[],
    predicate: (node: GenericTreeNode<T>) => boolean
): GenericTreeNode<T>[] | undefined => {
    for (const node of nodes) {
        if (predicate(node)) {
            return [node];
        }
        if (node.children) {
            const childResult = findPathWithDfs(node.children, predicate);
            if (childResult) {
                return [node].concat(childResult);
            }
        }
    }
};
